<template>
  <div class="mention-dropdown bg-primary-400 rounded-lg elevation-8">
    <div ref="scrollEl" class="mention-dropdown-scroll">
      <button
        v-for="(entity, index) in entities"
        :key="entity.id"
        type="button"
        class="mention-item d-flex align-center px-3 py-2 w-100 text-left"
        :class="{ 'mention-item--active': index === activeIndex }"
        @mousedown.prevent="$emit('select', entity)"
      >
        <v-icon size="14" color="tertiary-900" class="mr-2 flex-shrink-0">{{ entityIcon(entity) }}</v-icon>
        <span class="mention-item-name text-white text-body-2">{{ entity.name }}</span>
        <span
          v-if="entity.type === 'vehicle'"
          class="mention-item-hint text-primary-300 text-caption ml-2 flex-shrink-0"
        >{{ entity.id }}</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import type { ChatbotEntity } from "@/services/chatbot-service/models";

const props = defineProps<{
  entities: ChatbotEntity[];
  activeIndex: number;
}>();

defineEmits<{
  select: [entity: ChatbotEntity];
}>();

const scrollEl = ref<HTMLElement | null>(null);

// Group ids are prefixed by the API (EntitiesService): GRP_WS_* = worksites,
// GRP_BXTCAT_* / GRP_TYPE_* / GRP_ALL = fleet-wide groupings.
function entityIcon(entity: ChatbotEntity): string {
  if (entity.type !== "group") return "mdi-truck-outline";
  // mdi-truck-multiple(-outline) does not exist in MDI — car-multiple is the
  // closest existing "several vehicles" glyph.
  return entity.id.startsWith("GRP_WS_")
    ? "mdi-map-marker-outline"
    : "mdi-car-multiple";
}

watch(
  () => props.activeIndex,
  (index) => {
    const item = scrollEl.value?.children[index] as HTMLElement | undefined;
    item?.scrollIntoView({ block: "nearest" });
  },
);
</script>

<style scoped>
.mention-dropdown {
  border: 1px solid rgba(255, 255, 255, 0.1);
  overflow: hidden;
}

.mention-dropdown-scroll {
  max-height: 200px;
  overflow-y: auto;
}

.mention-item {
  background: transparent;
  border: none;
  cursor: pointer;
  transition: background 0.1s ease;
  width: 100%;
}

.mention-item:hover,
.mention-item--active {
  background: rgba(255, 255, 255, 0.07);
}

.mention-item-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.mention-item-hint {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
